import React, { useState } from "react";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Alert from "react-bootstrap/Alert";
import { usePortalAuth } from "../../contexts/PortalAuthContext";
import { portalLogin, portalForgotPassword } from "../../services/portalService";
import InputField from "../../utils/InputField";
import { useToast } from '../../contexts/ToastContext';
import { useLoading } from "../../contexts/LoadingContext";
import { useNavigate } from "react-router-dom";

function PortalLogin() {
  const { login } = usePortalAuth();
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();
  const navigate = useNavigate();

  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [product, setProduct] = useState("permit"); // default branding context for resets
  const [validationErrors, setValidationErrors] = useState({});
  const [serverError, setServerError] = useState("");

  const handleFieldChange = (field, value) => {
    if (field === "email") setEmail(value);
    if (field === "password") setPassword(value);
    if (field === "product") setProduct(value);
  };

  const validateForm = () => {
    const errors = {};
    if (!email) errors.email = "Email is required";
    if (!isForgotPassword && !password) errors.password = "Password is required";
    return errors;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setServerError("");

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      startLoading();
      const data = await portalLogin(email, password);
      showToast('Login successful!', 'success');
      
      // If backend reports mustChangePassword is true, temporarily hold the token 
      // or redirect them to set password. We'll direct to change password.
      if (data.mustChangePassword) {
        localStorage.setItem("portalToken", data.accessToken);
        showToast('Password change required on first login', 'warning');
        navigate("/portal/change-password");
      } else {
        login(data.accessToken);
      }
    } catch (error) {
      showToast(error.message || "Login failed. Please check your credentials.", 'error');
      setServerError(error.message || "Login failed. Please check your credentials.");
    } finally {
      stopLoading();
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setServerError("");

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      startLoading();
      await portalForgotPassword(email, product);
      showToast('If the email exists, a password reset link has been sent.', 'success');
      setIsForgotPassword(false);
    } catch (error) {
      showToast(error.message || "Failed to request password reset.", 'error');
      setServerError(error.message || "Failed to request password reset.");
    } finally {
      stopLoading();
    }
  };

  return (
    <section>
      <Container
        className="d-flex align-items-center justify-content-center login-background"
        fluid
      >
        <div className="login-modal">
          <div className="d-flex flex-column align-items-center justify-content-center mb-3">
            <img src={`${process.env.PUBLIC_URL}/logo.svg`} alt="Logo" className="login-logo mb-2" />
            <h4 className="text-secondary text-center mt-2 fw-semibold">Applicant Portal</h4>
          </div>

          {serverError && <Alert variant="danger">{serverError}</Alert>}

          {!isForgotPassword ? (
            <Form onSubmit={handleLoginSubmit}>
              <Row className="px-0 mb-3">
                <InputField
                  field="email"
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={handleFieldChange}
                  error={validationErrors.email}
                  as={Col}
                  controlId="portalLoginEmail"
                />
              </Row>
              <Row className="px-0 mb-3">
                <InputField
                  field="password"
                  label="Password"
                  type="password"
                  value={password}
                  onChange={handleFieldChange}
                  error={validationErrors.password}
                  as={Col}
                  controlId="portalLoginPassword"
                />
              </Row>
              <Button
                className="login-button mt-3 w-100"
                id="portalLoginButton"
                size="xl"
                type="submit"
              >
                Log In
              </Button>
              <div className="text-center mt-3">
                <a
                  href="#forgot"
                  className="text-decoration-none text-muted small"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsForgotPassword(true);
                    setServerError("");
                    setValidationErrors({});
                  }}
                >
                  Forgot Password?
                </a>
              </div>
            </Form>
          ) : (
            <Form onSubmit={handleForgotPasswordSubmit}>
              <Row className="px-0 mb-3">
                <InputField
                  field="email"
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={handleFieldChange}
                  error={validationErrors.email}
                  as={Col}
                  controlId="portalForgotEmail"
                />
              </Row>
              <Row className="px-0 mb-3">
                <Form.Group as={Col} controlId="portalForgotProduct">
                  <Form.Label className="small text-muted mb-1">Which service did you submit to?</Form.Label>
                  <Form.Select
                    value={product}
                    onChange={(e) => handleFieldChange("product", e.target.value)}
                    className="form-control"
                  >
                    <option value="permit">SimplePermit</option>
                    <option value="check">SimpleCheck</option>
                    <option value="spect">SimpleSpect</option>
                  </Form.Select>
                </Form.Group>
              </Row>
              <Button
                className="login-button mt-3 w-100"
                id="portalSendResetButton"
                size="xl"
                type="submit"
              >
                Send Reset Link
              </Button>
              <div className="text-center mt-3">
                <a
                  href="#login"
                  className="text-decoration-none text-muted small"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsForgotPassword(false);
                    setServerError("");
                    setValidationErrors({});
                  }}
                >
                  Back to Log In
                </a>
              </div>
            </Form>
          )}
        </div>
      </Container>
    </section>
  );
}

export default PortalLogin;
